
import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./referralHistory.css";

import { BHAREE_BASE_URL_LOCAL } from "../../../../config/config.url";

const ReferralHistory = () => {
  // Referral history
  const [referrals, setReferrals] = useState([]);
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [city, setCity] = useState("ALL");
  const [referralLoading, setReferralLoading] = useState(false);

  // Voucher requests
  const [voucherRequests, setVoucherRequests] = useState([]);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [couponCodes, setCouponCodes] = useState({});
  const [paymentReferences, setPaymentReferences] = useState({});

  const getToken = () => localStorage.getItem("jwtToken");

  const getAuthConfig = () => {
    const token = getToken();

    return {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // Fetch all referrals for admin
  const fetchReferralHistory = useCallback(async () => {
    const token = getToken();

    if (!token) {
      setReferrals([]);
      return;
    }

    setReferralLoading(true);

    try {
      const response = await axios.get(
        `${BHAREE_BASE_URL_LOCAL}/referrals/admin`,
        getAuthConfig()
      );

      if (response.data?.success) {
        setReferrals(response.data.data || []);
      } else {
        setReferrals([]);
      }
    } catch (error) {
      console.error(
        "ADMIN REFERRAL HISTORY ERROR:",
        error.response?.data || error.message
      );
      setReferrals([]);
    } finally {
      setReferralLoading(false);
    }
  }, []);

  // Fetch all voucher requests for admin
  const fetchVoucherRequests = useCallback(async () => {
    const token = getToken();

    if (!token) {
      setVoucherRequests([]);
      return;
    }

    setVoucherLoading(true);

    try {
      const response = await axios.get(
        `${BHAREE_BASE_URL_LOCAL}/voucher-requests/admin`,
        getAuthConfig()
      );

      if (response.data?.success) {
        setVoucherRequests(response.data.data || []);
      } else {
        setVoucherRequests([]);
      }
    } catch (error) {
      console.error(
        "FETCH VOUCHER REQUESTS ERROR:",
        error.response?.data || error.message
      );

      setVoucherRequests([]);
    } finally {
      setVoucherLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferralHistory();
    fetchVoucherRequests();
  }, [fetchReferralHistory, fetchVoucherRequests]);

  // Submit coupon code and process voucher request
  
  // Process Amazon voucher or Flipkart payout
  const handleSendVoucher = async (request) => {
    const voucherType = String(
      request.voucherType || request.voucher_type || ""
    ).trim().toUpperCase();

    const isFlipkart = voucherType === "FLIPKART";

    const couponCode = String(
      couponCodes[request.id] || ""
    ).trim();

    const paymentReference = String(
      paymentReferences[request.id] || ""
    ).trim();

    if (isFlipkart && !paymentReference) {
      alert("Please enter the payment reference.");
      return;
    }

    if (!isFlipkart && !couponCode) {
      alert("Please enter the Amazon coupon code.");
      return;
    }

    const confirmed = window.confirm(
      isFlipkart
        ? "Confirm that you have already paid ₹1000 to the user. Mark this request as Paid?"
        : "Save and issue this Amazon coupon code?"
    );

    if (!confirmed) return;

    setProcessingId(request.id);

    try {
      const payload = isFlipkart
        ? {
            action: "PAID",
            paymentReference,
          }
        : {
            action: "SEND",
            couponCode,
          };

      const response = await axios.patch(
        `${BHAREE_BASE_URL_LOCAL}/voucher-requests/admin/${request.id}`,
        payload,
        getAuthConfig()
      );

      if (response.data?.success) {
        alert(
          response.data.message ||
            (isFlipkart
              ? "Payout marked as Paid."
              : "Amazon voucher issued successfully.")
        );

        setCouponCodes((previous) => ({
          ...previous,
          [request.id]: "",
        }));

        setPaymentReferences((previous) => ({
          ...previous,
          [request.id]: "",
        }));

        await fetchVoucherRequests();
      } else {
        alert(
          response.data?.message ||
            "Unable to process this request."
        );
      }
    } catch (error) {
      console.error(
        "PROCESS VOUCHER ERROR:",
        error.response?.data || error.message
      );

      alert(
        error.response?.data?.message ||
          "Unable to process this request. Please try again."
      );
    } finally {
      setProcessingId(null);
    }
  };

  // Reject a pending redemption request
  const handleRejectVoucher = async (request) => {
    const confirmed = window.confirm(
      "Reject this redemption request? Any points already deducted will be refunded by the backend."
    );

    if (!confirmed) return;

    setProcessingId(request.id);

    try {
      const response = await axios.patch(
        `${BHAREE_BASE_URL_LOCAL}/voucher-requests/admin/${request.id}`,
        { action: "REJECT" },
        getAuthConfig()
      );

      if (response.data?.success) {
        alert(
          response.data.message || "Request rejected successfully."
        );

        await fetchVoucherRequests();
      } else {
        alert(
          response.data?.message || "Unable to reject this request."
        );
      }
    } catch (error) {
      console.error(
        "REJECT VOUCHER ERROR:",
        error.response?.data || error.message
      );

      alert(
        error.response?.data?.message ||
          "Unable to reject this request. Please try again."
      );
    } finally {
      setProcessingId(null);
    }
  };


  // Referral filters
  const filteredReferrals = referrals.filter((referral) => {
    const searchValue = search.toLowerCase().trim();

    const searchableValues = [
      referral.submittedByName,
      referral.submittedByMobile,
      referral.submittedByEmail,
      referral.submittedByRole,
      referral.refereeName,
      referral.refereeMobile,
      referral.refereeEmail,
      referral.company,
      referral.category,
      referral.city,
      referral.district,
    ];

    const matchesSearch =
      !searchValue ||
      searchableValues.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(searchValue)
      );

    const matchesUserType =
      userType === "ALL" ||
      String(referral.submittedByRole || "").toUpperCase() ===
        userType;

    const matchesStatus =
      status === "ALL" ||
      String(referral.status || "").toUpperCase() === status;

    const matchesCity =
      city === "ALL" || String(referral.city || "") === city;

    return (
      matchesSearch &&
      matchesUserType &&
      matchesStatus &&
      matchesCity
    );
  });

  // Unique cities
  const cities = [
    ...new Set(
      referrals.map((item) => item.city).filter(Boolean)
    ),
  ];

  // Export filtered referrals to PDF
  const handleExportPDF = () => {
    if (!filteredReferrals.length) {
      alert("No referral records available to export.");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Referral History", 14, 15);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Total Records: ${filteredReferrals.length}`,
      14,
      22
    );

    const filterText = [
      `User Type: ${userType}`,
      `Status: ${status}`,
      `City: ${city}`,
      search.trim() ? `Search: ${search.trim()}` : "",
    ]
      .filter(Boolean)
      .join(" | ");

    doc.text(filterText, 14, 28);

    const tableData = filteredReferrals.map((referral, index) => [
      index + 1,
      referral.submittedByName || "-",
      referral.submittedByRole || "-",
      referral.refereeName || "-",
      referral.refereeMobile || "-",
      referral.company || "-",
      referral.category || "-",
      referral.city || "-",
      referral.status || "Pending",
      referral.createdAt
        ? new Date(referral.createdAt).toLocaleDateString("en-IN")
        : "-",
    ]);

    autoTable(doc, {
      startY: 34,
      head: [
        [
          "#",
          "Submitted By",
          "Type",
          "Refer To",
          "Mobile",
          "Company",
          "Category",
          "City",
          "Status",
          "Created Date",
        ],
      ],
      body: tableData,
      theme: "grid",
      styles: {
        fontSize: 7,
        cellPadding: 2,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        fontSize: 7,
        fontStyle: "bold",
        halign: "center",
      },
      didDrawPage: () => {
        const pageNumber = doc.internal.getNumberOfPages();

        doc.setFontSize(8);
        doc.text(
          `Bharee - Referral History | Page ${pageNumber}`,
          14,
          doc.internal.pageSize.height - 8
        );
      },
    });

    doc.save(
      userType === "ALL"
        ? "Referral-History.pdf"
        : `Referral-History-${userType}.pdf`
    );
  };

  // Refresh both sections and reset referral filters
  const handleRefresh = () => {
    setSearch("");
    setUserType("ALL");
    setStatus("ALL");
    setCity("ALL");

    fetchReferralHistory();
    fetchVoucherRequests();
  };

  return (
    <Container fluid className="referral-history-page">
      {/* Dashboard header */}
      <Row className="referral-page-header align-items-center">
        <Col md={8}>
          <h1>Admin Dashboard</h1>
          <p>
            Manage B2B referrals, vouchers, and track user activities.
          </p>
        </Col>

        <Col md={4} className="text-end">
          <Button
            className="export-btn me-2"
            onClick={handleExportPDF}
          >
            ↓ Export Referrals
          </Button>

          <Button
            className="refresh-btn"
            onClick={handleRefresh}
          >
            ↻ Refresh
          </Button>
        </Col>
      </Row>

      <hr />

      {/* Voucher requests - same file and same page */}
      <Row className="mb-4">
        <Col>
          <h4 className="section-title">Voucher Requests</h4>

          <div className="voucher-table-wrapper">
            <Table responsive className="referral-table">
              <thead>
                <tr>
                  <th>USER ID / NAME</th>
                  <th>REQUEST DATE</th>
                  <th>VOUCHER TYPE</th>
                  <th>POINTS</th>
                 <th>COUPON CODE / PAYMENT REFERENCE</th>
                  <th>STATUS</th>
                  <th>ACTION</th>
                </tr>
              </thead>

              <tbody>
                {voucherLoading ? (
                  <tr>
                    <td colSpan={7} className="text-center">
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                      Loading voucher requests...
                    </td>
                  </tr>
                ) : voucherRequests.length > 0 ? (
                  voucherRequests.map((request) => {
                    const requestStatus = String(
                      request.status || "Pending"
                    ).toUpperCase();

                    const isPending = requestStatus === "PENDING";

                    return (
                      <tr key={request.id}>
                        <td>
                          <strong>
                            {request.requestedByName ||
                              request.name ||
                              request.userId ||
                              request.user_id ||
                              "-"}
                          </strong>
                          <div>
                            {request.userRole ||
                              request.user_role ||
                              request.requestedByRole ||
                              "-"}
                          </div>
                        </td>

                        <td>
                          {request.requestedAt ||
                          request.requested_at ||
                          request.createdAt ||
                          request.created_at
                            ? new Date(
                                request.requestedAt ||
                                  request.requested_at ||
                                  request.createdAt ||
                                  request.created_at
                              ).toLocaleDateString("en-IN")
                            : "-"}
                        </td>

                        <td className="green-text">
                          {request.voucherType ||
                            request.voucher_type ||
                            "-"}
                        </td>

                        <td className="green-text">
                       {request.pointsUsed ??
                        request.points_used ??
                       1000}
                        </td>

                       
  <td>
    {isPending ? (
      String(
        request.voucherType || request.voucher_type || ""
      ).toUpperCase() === "FLIPKART" ? (
        <Form.Control
          size="sm"
          type="text"
          placeholder="Payment reference after payout"
          value={paymentReferences[request.id] || ""}
          onChange={(event) =>
            setPaymentReferences((previous) => ({
              ...previous,
              [request.id]: event.target.value,
            }))
          }
        />
      ) : (
        <Form.Control
          size="sm"
          type="text"
          placeholder="Enter Amazon coupon code"
          value={couponCodes[request.id] || ""}
          onChange={(event) =>
            setCouponCodes((previous) => ({
              ...previous,
              [request.id]: event.target.value,
            }))
          }
        />
      )
    ) : (
      String(
        request.voucherType || request.voucher_type || ""
      ).toUpperCase() === "FLIPKART"
        ? request.paymentReference ||
          request.payment_reference ||
          "-"
        : request.couponCode ||
          request.coupon_code ||
          "-"
    )}
  </td>


                        <td>
                          <span className="status-badge">
                            ● {request.status || "Pending"}
                          </span>
                        </td>

                        
  <td>
    {isPending ? (
      <div className="d-flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="success"
          disabled={processingId === request.id}
          onClick={() => handleSendVoucher(request)}
        >
          {processingId === request.id
            ? "Processing..."
            : String(
                request.voucherType ||
                  request.voucher_type ||
                  ""
              ).toUpperCase() === "FLIPKART"
            ? "Mark as Paid"
            : "Send Voucher"}
        </Button>

        <Button
          size="sm"
          variant="danger"
          disabled={processingId === request.id}
          onClick={() => handleRejectVoucher(request)}
        >
          Reject
        </Button>
      </div>
    ) : (
      <span className="text-muted">Processed</span>
    )}
  </td>

                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="no-data">
                      No voucher requests found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>

      {/* Referral filters */}
      <Row className="filter-row align-items-center mb-4">
        <Col md={5}>
          <Form.Control
            type="text"
            placeholder="Search referrer, referee, company..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="search-input"
          />
        </Col>

        <Col md={2}>
          <Form.Select
            value={userType}
            onChange={(event) => setUserType(event.target.value)}
            className="filter-select"
          >
            <option value="ALL">All Users</option>
            <option value="BUYER">Buyer</option>
            <option value="SELLER">Seller</option>
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="filter-select"
          >
            <option value="ALL">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="CONTACTED">Contacted</option>
            <option value="REGISTERED">Registered</option>
            <option value="ACTIVE">Active</option>
            <option value="REJECTED">Rejected</option>
          </Form.Select>
        </Col>

        <Col md={3}>
          <Form.Select
            value={city}
            onChange={(event) => setCity(event.target.value)}
            className="filter-select"
          >
            <option value="ALL">All Cities</option>
            {cities.map((cityName) => (
              <option key={cityName} value={cityName}>
                {cityName}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Referral activities */}
      <Row>
        <Col>
          <h4 className="section-title">Referral Activities</h4>

          <div className="referral-table-wrapper">
            <Table responsive className="referral-table">
              <thead>
                <tr>
                  <th>SUBMITTED BY</th>
                  <th>TYPE</th>
                  <th>REFER TO</th>
                  <th>MOBILE</th>
                  <th>COMPANY</th>
                  <th>CATEGORY</th>
                  <th>CITY</th>
                  <th>DATE</th>
                  <th>POINTS</th>
                  <th>STATUS</th>
                </tr>
              </thead>

              <tbody>
                {referralLoading ? (
                  <tr>
                    <td colSpan={10} className="text-center">
                      Loading referral history...
                    </td>
                  </tr>
                ) : filteredReferrals.length > 0 ? (
                  filteredReferrals.map((referral) => (
                    <tr key={referral.id}>
                      <td>
                        <strong>
                          {referral.submittedByName || "-"}
                        </strong>
                      </td>

                      <td>
                        <span className="refer-type-badge">
                          {referral.submittedByRole || "-"}
                        </span>
                      </td>

                      <td>{referral.refereeName || "-"}</td>
                      <td>{referral.refereeMobile || "-"}</td>
                      <td>{referral.company || "-"}</td>
                      <td>{referral.category || "-"}</td>
                      <td>{referral.city || "-"}</td>

                      <td>
                        {referral.createdAt
                          ? new Date(
                              referral.createdAt
                            ).toLocaleDateString("en-IN")
                          : "-"}
                      </td>

                     
<td className="green-text">
  +{
    referral.points_awarded ??
    referral.pointsAwarded ??
    referral.points ??
    0
  } pts
</td>


                      <td>
                        <span className="status-badge">
                          ● {referral.status || "Pending"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="no-data">
                      No referral history found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>

      <div className="records-footer">
        Showing <strong>{filteredReferrals.length}</strong> of{" "}
        <strong>{referrals.length}</strong> referral records
      </div>
    </Container>
  );
};

export default ReferralHistory;
